import { v4 as uuidv4 } from "uuid"
import { animalGenerator } from "../components/Views/AnimalManagement/demo/animalGenerator"
import { animals, species } from "../fakeBDD/animals"
import { getCreationStamp, getModificationStamp, getZooId, initDb } from "../tools/conf"
import { ANIMAL_TEMPLATE } from "./dataTemplate/animal"
import { SurveyDatabaseConnector } from "./surveyDatabaseConnector"
import _ from "lodash"
import { ImageController } from "./imageController.js"
import { FilesController } from "./filesController"
import PHOTOPLACEHOLDER from "../assets/placeholders/photo.jpg"
import { buildDemoGalleryMedia } from "../anonymization"
import { getAnimalsQueryScope } from "../utils/firestore/animalsZooScope"
import {
  buildZimsRestorePatch,
  comparableGan,
  describeGanHolder,
  GAN_HOLDER_STATUSES,
  pickGanHolder,
  toAnimalEnclosureData,
} from "../utils/animals/ganConflict"
import moment from "moment"
import firebase from "firebase/compat/app"
import "firebase/compat/firestore"

// browser-image-compression (≈235 Ko) n'est charge qu'au premier envoi de
// photo, pas avec le chunk principal (decision du 2026-09-05).
const imageCompression = (...args) =>
  import("browser-image-compression").then((module) => (module.default || module)(...args))

const animlaGen = new animalGenerator()
const ImageControl = new ImageController()
const cdn = new FilesController()

const { uniqueNamesGenerator, adjectives, colors } = require("unique-names-generator")

export class AnimalDatabaseConnector {
  // Logique mutualisée dans utils/firestore/animalsZooScope (même contrat :
  // { canReadAllZoos, zooId }, jette ANIMAL_QUERY_ZOO_REQUIRED sans zoo).
  async _getAnimalQueryScope() {
    return getAnimalsQueryScope()
  }

  // SpeciesData
  async _getSpeciesList() {
    // not used
    try {
      let speciesData = []
      await species.map(async (item) => {
        let animalsFromThisSpecies = await this.getAnimalsListFromSpecieId(item.specieId, true)
        let animalAmount
        animalsFromThisSpecies.success === true ? (animalAmount = animalsFromThisSpecies.data.length) : (animalAmount = 0)
        let newEntry = {
          ...item,
          animalAmount: animalAmount,
        }
        speciesData.push(newEntry)
      })
      return {
        success: true,
        data: speciesData,
      }
    } catch (error) {
      console.log(error)
    }
  }

  /////////////////
  // AnimalsData //
  /////////////////

  getAnimalProfilePicture = async (animalData) => {
    const picture = animalData.animalProfilePicture
    if (picture !== false) {
      if (_.has(picture, "bddRef")) {
        let uri = await ImageControl.getUrlFromStorageRef(picture.bddRef)
        return {
          ...animalData,
          animalProfilePicture: {
            ...picture,
            uri: uri,
          },
        }
      }
      // Fiche créée en Nouvelle avant COL-10 : la photo est restée en data URI
      // dans la fiche, marquée `shouldUpload`. On la garde telle quelle : elle
      // s'affiche, et la prochaine sauvegarde envoie la VRAIE photo dans
      // Storage. La remplacer par le pictogramme en gardant `shouldUpload`
      // faisait envoyer le pictogramme à la place (recette 12.1.0, COL-13).
      if (typeof picture?.uri === "string" && picture.uri.startsWith("data:image/")) {
        return animalData
      }
    }
    // Le pictogramme n'est qu'un affichage : il ne doit jamais partir en envoi.
    const { shouldUpload: _pendingUpload, ...placeholderBase } = picture || {}
    return {
      ...animalData,
      animalProfilePicture: {
        ...placeholderBase,
        uri: PHOTOPLACEHOLDER,
      },
    }
  }

  async _getAnimalDataFromId(animalId) {
    let db = initDb()
    if (Array.isArray(animalId)) {
      let arrayRes = []
      await Promise.all(
        animalId.map(async (el) => {
          let snapshot = await db.collection("animals").doc(el).get()
          let data = snapshot.data()
          if (!data) {
            arrayRes.push(null)
            return
          }
          data = await this.getAnimalProfilePicture(data)
          arrayRes.push(data)
        })
      )
      return arrayRes
    } else {
      try {
        let snapshot = await db.collection("animals").doc(animalId).get()
        let data = snapshot.data()
        if (!data) return null
        data = await this.getAnimalProfilePicture(data)
        return data
      } catch (error) {
        console.log(error)
        return null
      }
    }
  }

  async _getAnimalMediaFromId(animalId, animalData) {
    let db = initDb()
    try {
      let list = []
      let snapshot = await db.collection("animals").doc(animalId).collection("media").get()
      snapshot.forEach((el) => {
        let data = el.data()
        if (_.has(data.media, "url")) {
          let newEntry = {
            ...data,
            animalName: animalData.animalName,
          }
          list.push(newEntry)
        }
      })

      console.log(list.length)

      await Promise.all(
        list.map(async (el) => {
          if (el.media.secured) {
            try {
              let url = await ImageControl.getUrlFromStorageRef(el.media.bddRef)
              el.media.url = url
            } catch (error) {
              console.log(error)
              return null
            }
          } else if (el.media.url.slice(0, 2) === "gs") {
            try {
              let url = await ImageControl.getUrlFromStorageRef(el.media.url)
              el.media.url = url
            } catch (error) {
              console.log(error)
            }
          } else {
            console.log("missing something")
            console.log(el)
          }
        })
      )

      // Mode démo : galerie vide → médias factices (retourne [] hors démo)
      if (list.length === 0) {
        const demoItems = buildDemoGalleryMedia({
          seed: `animal-media-${animalId}`,
          species: [animalData.specieName, animalData.specieLatinName],
          kind: "animal",
        })
        return demoItems.map((item) => ({
          media: { url: item.url, type: "image" },
          user: item.userName,
          timestamp: item.timestamp,
          animalName: animalData.animalName,
        }))
      }

      return list
    } catch (error) {
      console.log(error)
      return []
    }
  }

  async _getAnimalsFromEnclosureId(enclosureId, { includeInactive = false } = {}) {
    let db = initDb()
    try {
      const { zooId } = await this._getAnimalQueryScope()
      let snapshot = await db.collection("animals")
        .where("zooId", "==", zooId)
        .where("animalEnclosureData.enclosureId", "==", enclosureId)
        .get()
      let list = []
      snapshot.forEach((doc) => {
        list.push(doc.data())
      })
      if (!includeInactive) {
        list = list.filter(a => !a.isDeceased && !a.isDeparted && !a.isArchived)
      }
      return list
    } catch (error) {
      console.log(error)
      return []
    }
  }

  async _getLastEvalsDetailsAverage(animalIds, limit) {
    await Promise.all(
      animalIds.map(async (id) => {
        let data = await this._getLastEvals(id, limit)
      })
    )
  }

  async _getSameSpeciesId(specieId, zooId) {
    let db = initDb()
    let snapshot = await db.collection("animals").where("specieId", "==", specieId).where("zooId", "==", zooId).get()
    let list = []
    snapshot.forEach((el) => {
      list.push(el.data())
    })
    return list
  }

  async _getLastEvals(animalId, limit, start, stop, filterTraining, filterPedago) {
    try {
      let db = initDb()

      let snapshot
      if (limit === null) {
        if (filterTraining) {
          if (filterPedago) {
            console.log("cas 1")
            snapshot = await db.collection("quizz").where("animalId", "==", animalId).orderBy("quizzStop").where("quizzStop", ">", start).where("quizzStop", "<", stop).where("isValid", "==", true).where("isPedago", "==", false).get()
          } else {
            console.log("cas 2")
            snapshot = await db.collection("quizz").where("animalId", "==", animalId).orderBy("quizzStop").where("quizzStop", ">", start).where("quizzStop", "<", stop).where("isValid", "==", true).where("isPedago", "==", true).get()
          }
        } else {
          if (filterPedago) {
            console.log("cas 3")
            snapshot = await db.collection("quizz").where("animalId", "==", animalId).orderBy("quizzStop").where("quizzStop", ">", start).where("quizzStop", "<", stop).where("isPedago", "==", false).get()
          } else {
            console.log("cas 4")
            snapshot = await db.collection("quizz").where("animalId", "==", animalId).orderBy("quizzStop").where("quizzStop", ">", start).where("quizzStop", "<", stop).where("isPedago", "==", true).get()
          }
        }
      } else {
        if (filterTraining) {
          if (filterPedago) {
            console.log("cas 5")
            snapshot = await db.collection("quizz").where("animalId", "==", animalId).orderBy("quizzStop").limit(limit).where("isValid", "==", true).where("isPedago", "==", false).get()
          } else {
            console.log("cas 6")
            snapshot = await db.collection("quizz").where("animalId", "==", animalId).orderBy("quizzStop").limit(limit).where("isValid", "==", true).where("isPedago", "==", true).get()
          }
        } else {
          if (filterPedago) {
            console.log("cas 7")
            snapshot = await db.collection("quizz").where("animalId", "==", animalId).where("isPedago", "==", false).orderBy("quizzStop").limit(limit).get()
          } else {
            console.log("cas 8")
            snapshot = await db.collection("quizz").where("animalId", "==", animalId).where("isPedago", "==", false).orderBy("quizzStop").limit(limit).get()
          }
        }
      }

      let list = []
      if (snapshot.empty) {
        return null
      } else {
        snapshot.forEach(async (doc) => {
          list.push(doc.data())
        })
        let populatedList = []
        for (let index = 0; index < list.length; index++) {
          const el = list[index]
          let details = await (await db.collection("quizz").doc(el.syncId).collection("quizzData").doc("stats").get()).data()
          populatedList.push({
            ...el,
            details: details,
          })
        }
        return populatedList
      }
    } catch (error) {
      console.log(error)
      return []
    }
  }

  async _getLastEvalsBySpecie(specieId, limit) {
    let db = initDb()
    try {
      let snapshot = await db.collection("quizz").where("specieId", "==", specieId).orderBy("quizzStop").limit(limit).get()
      let list = []
      if (snapshot.empty) {
        return null
      } else {
        snapshot.forEach(async (doc) => {
          list.push(doc.data())
        })
        let populatedList = []
        for (let index = 0; index < list.length; index++) {
          const el = list[index]
          let details = await (await db.collection("quizz").doc(el.syncId).collection("quizzData").doc("stats").get()).data()
          populatedList.push({
            ...el,
            details: details,
          })
        }
        return populatedList
      }
    } catch (error) {
      return []
    }
  }

  async _getLastObs(animalId, limit, start, stop, filterTraining, filterPedago) {
    try {
      let db = initDb()
      let snapshot
      if (limit !== null) {
        if (filterTraining) {
          if (filterPedago) {
            snapshot = await db.collection("obs").where("animalId", "==", animalId).orderBy("obsStop").limit(limit).where("isValid", "==", true).where("isPedago", "==", false).get()
          } else {
            snapshot = await db.collection("obs").where("animalId", "==", animalId).orderBy("obsStop").limit(limit).where("isValid", "==", true).where("isPedago", "==", true).get()
          }
        } else {
          if (filterPedago) {
            snapshot = await db.collection("obs").where("animalId", "==", animalId).where("isPedago", "==", false).orderBy("obsStop").limit(limit).get()
          } else {
            snapshot = await db.collection("obs").where("animalId", "==", animalId).where("isPedago", "==", true).orderBy("obsStop").limit(limit).get()
          }
        }

        let list = []
        if (snapshot.empty) {
          return null
        } else {
          snapshot.forEach((doc) => {
            list.push(doc.data())
          })
          return list
        }
      } else {
        if (filterTraining) {
          if (filterPedago) {
            snapshot = await db.collection("obs").where("animalId", "==", animalId).orderBy("obsStop").where("obsStop", ">", start).where("obsStop", "<", stop).where("isValid", "==", true).where("isPedago", "==", false).get()
          } else {
            snapshot = await db.collection("obs").where("animalId", "==", animalId).orderBy("obsStop").where("obsStop", ">", start).where("obsStop", "<", stop).where("isValid", "==", true).where("isPedago", "==", true).get()
          }
        } else {
          if (filterPedago) {
            snapshot = await db.collection("obs").where("animalId", "==", animalId).orderBy("obsStop").where("obsStop", ">", start).where("obsStop", "<", stop).where("isPedago", "==", false).get()
          } else {
            snapshot = await db.collection("obs").where("animalId", "==", animalId).orderBy("obsStop").where("obsStop", ">", start).where("obsStop", "<", stop).where("isPedago", "==", true).get()
          }
        }

        let list = []
        if (snapshot.empty) {
          return null
        } else {
          snapshot.forEach((doc) => {
            list.push(doc.data())
          })

          // middleWare

          let durationArray = []
          let totalDuration = 0
          list.map((obs) => {
            obs.obsData.map((el) => {
              let duration = el.absoluteTimeStop - el.absoluteTimeStart
              durationArray.push(duration)
              totalDuration = totalDuration + duration
            })
          })

          durationArray.sort((a, b) => a - b)
          return list
        }
      }

      //
    } catch (error) {
      console.log(error)
      return []
    }
  }

  async _getLastObsBySpecie(specieId, limit) {
    let db = initDb()
    try {
      let snapshot = await db.collection("obs").where("specieId", "==", specieId).orderBy("obsStop").limit(limit).get()
      let list = []
      if (snapshot.empty) {
        return null
      } else {
        snapshot.forEach((doc) => {
          list.push(doc.data())
        })
        return list
      }
    } catch (error) {
      console.log(error)
      return []
    }
  }

  async _getAnimalDataFromGan(animalGan) {
    let db = initDb()
    try {
      const { zooId } = await this._getAnimalQueryScope()
      let snapshot = await db.collection("animals")
        .where("zooId", "==", zooId)
        .where("animalGan", "==", animalGan)
        .get()
      let list = []
      if (snapshot.empty) {
        return null
      } else {
        snapshot.forEach((doc) => {
          list.push(doc.data())
        })
        return list[0]
      }
    } catch (error) {
      console.log(error)
    }
  }

  async _getAnimalSurveyWhiteList(animalId) {
    const surveyConnector = new SurveyDatabaseConnector()
    try {
      let { animalSpecieId } = await this._getAnimalData(animalId)
      let { specieSurveyWhiteList } = species[animalSpecieId]

      const surveyArray = []

      await specieSurveyWhiteList.map(async (surveyId) => {
        let newSurvey = await surveyConnector._getSurveyFromId(surveyId)
        surveyArray.push(newSurvey)
      })

      return {
        success: true,
        data: surveyArray,
      }
    } catch (error) {
      return {
        success: false,
        data: error,
      }
    }
  }

  async getAnimalsListFromSpecieId(specieId, lengthOnly) {
    // not used
    let animalList = []

    try {
      animals.map((item) => {
        return item.animalSpecieId === specieId ? animalList.push(item) : null
      })
      if (animalList.length > 0) {
        return {
          success: true,
          data: animalList,
        }
      } else {
        return {
          success: false,
          data: null,
        }
      }
    } catch (error) {
      return {
        success: false,
        data: error,
      }
    }
  }

  async _getAnimalsListFromGroupId(groupId, { includeInactive = false } = {}) {
    let db = await initDb()
    const { zooId } = await this._getAnimalQueryScope()
    let snapshot = await db.collection("animals")
      .where("zooId", "==", zooId)
      .where("animalGroupId", "==", groupId)
      .where("isArchived", "!=", true)
      .get()
    let list = []
    snapshot.forEach((doc) => {
      list.push(doc.data())
    })
    if (!includeInactive) {
      list = list.filter(a => !a.isDeceased && !a.isDeparted)
    }
    return list
  }

  async _getGroupsListFromSpecieId(specieId, zooFilter) {
    let db = await initDb()
    // « all » (groupes de tous les parcs) : staff AKONGO seulement, comme
    // _getAnimalsListFromSpecieId. Les règles par parc (niveau 1) refusent cette
    // liste aux autres comptes, superAdmin de parc compris : ils restent dans leur parc.
    const { canReadAllZoos, zooId } = await this._getAnimalQueryScope()
    if (zooFilter === "all" && canReadAllZoos) {
      var snapshot = await db.collection("groups").where("specieIds", "array-contains", specieId).get()
    } else {
      snapshot = await db.collection("groups").where("specieIds", "array-contains", specieId).where("zooId", "==", zooId).get()
    }
    let list = []
    snapshot.forEach((doc) => {
      list.push(doc.data())
    })
    return list
  }

  async _getAnimalsListFromSpecieId(specieId, zooFilter, groupId, { includeInactive = false } = {}) {
    try {
      let db = await initDb()
      const { canReadAllZoos, zooId } = await this._getAnimalQueryScope()

      if (zooFilter === "group") {
        var snapshot = await db.collection("animals")
          .where("zooId", "==", zooId)
          .where("animalGroupId", "==", groupId)
          .where("isArchived", "!=", true)
          .get()
      }

      if (zooFilter === "all") {
        let query = db.collection("animals")
        if (!canReadAllZoos) query = query.where("zooId", "==", zooId)
        snapshot = await query.where("specieId", "==", specieId).where("isArchived", "!=", true).get()
      }

      if (zooFilter !== "group" && zooFilter !== "all") {
        snapshot = await db.collection("animals")
          .where("zooId", "==", zooId)
          .where("specieId", "==", specieId)
          .where("isArchived", "!=", true)
          .get()
      }

      let list = []
      snapshot.forEach((doc) => {
        list.push(doc.data())
      })

      if (!includeInactive) {
        list = list.filter(a => !a.isDeceased && !a.isDeparted)
      }

      return list
    } catch (error) {
      console.log(error)
      return []
    }
  }

  async _getAnimalsList(zooId, { includeInactive = false } = {}) {
    let db = initDb()
    if (zooId === undefined) {
      zooId = await getZooId()
    }

    let list = []

    if (zooId !== "demo") {
      try {
        // Récupérer les animaux du zoo actuel
        let snapshot = await db.collection("animals").where("zooId", "==", zooId).where("isArchived", "!=", true).get()
        if (!snapshot.empty) {
          snapshot.forEach((doc) => {
            let animalData = doc.data()
            let newEntry = {
              ...animalData,
              label: animalData.animalName,
              value: animalData.animalId,
            }
            list.push(newEntry)
          })
        }

        // Récupérer également les animaux transférés où ce zoo est autorisé
        if (includeInactive) {
          let departedSnapshot = await db.collection("animals")
            .where("authorizedZooIds", "array-contains", zooId)
            .where("isDeparted", "==", true)
            .where("isArchived", "!=", true)
            .get()

          if (!departedSnapshot.empty) {
            departedSnapshot.forEach((doc) => {
              let animalData = doc.data()
              // Éviter les doublons (si l'animal est toujours dans le zoo actuel)
              if (!list.some(a => a.animalId === animalData.animalId)) {
                let newEntry = {
                  ...animalData,
                  label: animalData.animalName,
                  value: animalData.animalId,
                }
                list.push(newEntry)
              }
            })
          }
        }

        // Filtrer les animaux décédés/partis par défaut
        if (!includeInactive) {
          list = list.filter(a => !a.isDeceased && !a.isDeparted)
        }

        list.sort(function (a, b) {
          if (a.label < b.label) {
            return -1
          }
          if (a.label > b.label) {
            return 1
          }
          return 0
        })

        return list
      } catch (error) {
        console.log(error)
        return {
          success: false,
          data: error,
        }
      }
    } else {
      return await animlaGen._generateAnimals(80)
    }
  }

  /**
   * Relie une fiche existante à son animal ZIMS en y écrivant le GAN (#1234) :
   * la collection ZIMS la reconnaît ensuite « Dans AWM » au lieu de proposer un
   * doublon à l'import. Revérifié sur Firestore : fiche du parc, sans autre GAN,
   * et GAN porté par aucune autre fiche du parc. L'ID local ZIMS complète la
   * fiche s'il y manque ; la puce et le nom ne sont pas touchés.
   */
  async _linkAnimalToZimsGan({ zooId, animalId, gan, localId = null }) {
    const db = initDb()
    const cleanGan = String(gan || "").trim()
    if (!zooId || !animalId || !cleanGan) {
      return { success: false, code: "invalid-link", message: "Missing park, animal or GAN" }
    }
    const ref = db.collection("animals").doc(animalId)
    const doc = await ref.get()
    if (!doc.exists || doc.data()?.zooId !== zooId) {
      return { success: false, code: "animal-scope-mismatch", message: "Animal not found in this park" }
    }
    const animal = doc.data()
    const currentGan = String(animal.animalGan || animal.zimsID || "").trim()
    if (currentGan === cleanGan) return { success: true, animalId, alreadyLinked: true }
    if (currentGan) {
      return { success: false, code: "animal-already-linked", message: `Animal already linked to GAN ${currentGan}` }
    }
    const duplicate = await db
      .collection("animals")
      .where("zooId", "==", zooId)
      .where("animalGan", "==", cleanGan)
      .limit(1)
      .get()
    if (!duplicate.empty) {
      return { success: false, code: "duplicate-gan", message: `An animal with GAN ${cleanGan} already exists in this park` }
    }
    const patch = { animalGan: cleanGan, ...getModificationStamp() }
    const cleanLocalId = String(localId || "").trim()
    if (cleanLocalId && !String(animal.animalLocalId || "").trim()) patch.animalLocalId = cleanLocalId
    await ref.update(patch)
    return { success: true, animalId }
  }

  /**
   * Fiche du parc qui porte déjà ce GAN (#1235), tous états confondus : active,
   * décédée, partie ou archivée (« supprimée » à l'écran). `excludeAnimalId`
   * écarte la fiche en cours de modification. Un GAN vide ou de remplissage
   * (« N/C », « 0 »…) n'est jamais comparé. Scopé au parc : un même GAN existe
   * légitimement dans un autre parc après un transfert.
   */
  async _findAnimalWithGan({ zooId, gan, excludeAnimalId = null } = {}) {
    const cleanGan = String(gan || "").trim()
    if (!zooId || !comparableGan(cleanGan)) return null
    const db = initDb()
    const snapshot = await db
      .collection("animals")
      .where("zooId", "==", zooId)
      .where("animalGan", "==", cleanGan)
      .limit(10)
      .get()
    const candidates = (snapshot.docs || [])
      .map((doc) => ({ id: doc.id, data: doc.data() || {} }))
      .filter(({ id, data }) => !excludeAnimalId || (id !== excludeAnimalId && data.animalId !== excludeAnimalId))
    // Doublons anciens possibles : une fiche non archivée fait foi avant une archivée.
    const holder = pickGanHolder(candidates)
    if (!holder) return null
    return { ...describeGanHolder(holder.data, holder.id), docId: holder.id, data: holder.data }
  }

  /**
   * Revérifie avant une écriture qu'aucune autre fiche du parc ne porte ce GAN.
   * Renvoie le résultat d'échec à transmettre à l'écran, ou null si le GAN est
   * libre. Une lecture impossible ne bloque pas l'enregistrement (best-effort,
   * comme le garde-fou de l'import ZIMS).
   */
  async _ganConflict({ zooId, gan, excludeAnimalId = null } = {}) {
    try {
      const holder = await this._findAnimalWithGan({ zooId, gan, excludeAnimalId })
      if (!holder) return null
      const { data: _data, docId: _docId, ...publicHolder } = holder
      return {
        success: false,
        code: "duplicate-gan",
        holder: publicHolder,
        message: `An animal with GAN ${String(gan).trim()} already exists in this park`,
      }
    } catch (error) {
      console.error("[animalDatabaseConnector] GAN check failed:", error)
      return null
    }
  }

  /**
   * L'animal importé depuis la collection ZIMS a déjà une fiche archivée dans le
   * parc (#1236) : la fiche est restaurée au lieu d'un refus « GAN déjà connu ».
   * Elle garde son nom, son espèce et son historique ; elle reprend l'enclos
   * choisi à l'import (cf. `buildZimsRestorePatch`).
   */
  async _restoreArchivedAnimalFromZims(holder, animalZimsData) {
    const db = initDb()
    const stamp = getModificationStamp() || {}
    const patch = buildZimsRestorePatch(holder.data, animalZimsData, {
      restoredBy: stamp.lastModifiedBy || null,
    })
    await db.collection("animals").doc(holder.docId).update({ ...patch, ...stamp })
    const animalId = holder.animalId || holder.docId
    return {
      success: true,
      restored: true,
      animalId,
      animalName: holder.animalName,
      archivedDate: holder.archivedDate,
      data: { ...holder.data, ...patch, animalId },
    }
  }

  async _importAnimalFromZims(animalZimsData) {
    let db = initDb()
    let zooId = await getZooId()
    let uuid = uuidv4()

    // Garde-fou serveur anti-doublon : la déduplication UI repose sur une liste
    // en cache qui exclut les animaux archivés — on revérifie ici sur Firestore,
    // tous états confondus. Une fiche archivée de cet animal est restaurée
    // (#1236) ; toute autre fiche porteuse du GAN bloque l'import.
    const gan = (animalZimsData.animalGan || "").trim()
    if (gan) {
      let holder = null
      try {
        holder = await this._findAnimalWithGan({ zooId, gan })
      } catch (error) {
        // Garde best-effort : une erreur de vérification ne bloque pas l'import
        console.error("[_importAnimalFromZims] duplicate GAN check failed:", error)
      }
      if (holder?.status === GAN_HOLDER_STATUSES.ARCHIVED) {
        try {
          return await this._restoreArchivedAnimalFromZims(holder, animalZimsData)
        } catch (error) {
          console.error("[_importAnimalFromZims] restore failed:", error)
          return { success: false, data: error, message: error?.message || String(error) }
        }
      }
      if (holder) {
        const { data: _data, docId: _docId, ...publicHolder } = holder
        return {
          success: false,
          code: "duplicate-gan",
          holder: publicHolder,
          message: `An animal with GAN ${gan} already exists in this zoo`,
        }
      }
    }

    // Même forme minimale que le flux standard de création (cf. cleanAnimalData
    // dans AnimalManagementView) : ne jamais embarquer l'objet enclos complet
    // (media, config, champs volatils…) dans le doc animal.
    const animalEnclosureData = toAnimalEnclosureData(animalZimsData.animalEnclosureData)

    let animal = {
      ...ANIMAL_TEMPLATE,
      // Les trois flags de statut sont toujours écrits : le compteur « Tous »
      // mobile (countAnimalsByZooId) compte par `== false`, ce qui exclut un
      // doc où le champ est absent.
      isArchived: false,
      isDeceased: false,
      isDeparted: false,
      animalGan: animalZimsData.animalGan,
      animalName: animalZimsData.animalName,
      animalLocalId: animalZimsData.animalLocalId || null,
      // Puce/transpondeur : remonté par la collection ZIMS quand il existe,
      // null sinon (les tunnels d'import plus anciens ne le fournissent pas).
      animalChipId: animalZimsData.animalChipId || null,
      zooId: zooId,
      animalId: uuid,
      specieName: animalZimsData.specieName,
      specieId: animalZimsData.specieId,
      orderId: animalZimsData.orderId,
      familyId: animalZimsData.familyId,
      animalEnclosureData: animalEnclosureData,
      animalSexe: animalZimsData.animalSexe,
      // Champs d'espèce supplémentaires
      specieLatinName: animalZimsData.specieLatinName,
      specieEnglishName: animalZimsData.specieEnglishName,
      specieFamily: animalZimsData.specieFamily,
      specieMedia: animalZimsData.specieMedia || null,
      specieType: animalZimsData.specieType || null,
      // Taxonomies
      akongoTaxonomyEnabled: animalZimsData.akongoTaxonomyEnabled !== undefined ? animalZimsData.akongoTaxonomyEnabled : true,
      iucnTaxonomyEnabled: animalZimsData.iucnTaxonomyEnabled || false,
      iucn: animalZimsData.iucn || null,
      // Sous-espèce
      subspeciesLatinName: animalZimsData.subspeciesLatinName || '',
      customSpecie: animalZimsData.customSpecie || '',
      // Label formaté
      label: animalZimsData.label || '',
      // ID de batch pour import en masse (skip version sync côté CF)
      _massTransferBatchId: animalZimsData._massTransferBatchId || null,
      // Appartenance à enclos/groupe
      animalBelongToEnclosure: animalEnclosureData ? true : false,
      animalBelongToGroup: false,
      animalGroupData: null,
      animalGroupId: null,
      // Autres champs optionnels de l'espèce
      iucnMatched: animalZimsData.iucnMatched || false,
      iucnMatchedAt: animalZimsData.iucnMatchedAt || null,
      iucnId: animalZimsData.iucnId || null,
      specieHtmlDescription: animalZimsData.specieHtmlDescription || null,
      specieMediaCompletion: animalZimsData.specieMediaCompletion || 0,
      specieReadyForEBEA: animalZimsData.specieReadyForEBEA || false,
      ethogramAvailability: animalZimsData.ethogramAvailability || false,
      quizzAvailability: animalZimsData.quizzAvailability || false,
      ebeaStatus: animalZimsData.ebeaStatus || false,
    }

    // Date de naissance : champ ABSENT si ZIMS n'en fournit pas (format
    // historique sain) — jamais null ni undefined (une birthdate null a déjà
    // fait planter la liste des individus, cf. PR #142).
    if (animalZimsData.animalDob) {
      animal.animalBirthdate = animalZimsData.animalDob
    }

    // Firestore (SDK compat) rejette les valeurs undefined : on omet les
    // champs que l'appelant n'a pas renseignés plutôt que de faire échouer
    // l'écriture ou d'inventer une valeur.
    Object.keys(animal).forEach((key) => {
      if (animal[key] === undefined) {
        delete animal[key]
      }
    })

    delete animal.animalLatinName

    // Import ZIMS : l'auteur tracé est celui qui a lancé l'import.
    animal = { ...animal, ...getCreationStamp(), ...getModificationStamp() }

    try {
      await db.collection("animals").doc(uuid).set(animal)
      return { success: true, data: animal, animalId: uuid }
    } catch (error) {
      console.error(error)
      // message exposé pour que les tunnels d'import affichent la cause réelle
      return { success: false, data: error, message: error?.message || String(error) }
    }
  }

  async _setNewAnimal(animalData) {
    let db = initDb()
    let zooId = await getZooId()
    let uuid = uuidv4()

    // Un GAN n'appartient qu'à une fiche du parc (#1235).
    const conflict = await this._ganConflict({ zooId, gan: animalData?.animalGan })
    if (conflict) return conflict

    let newEntry = {
      ...animalData,
      zooId: zooId,
      animalId: uuid,
      // Flags de statut toujours présents, cf. _importAnimalFromZims.
      isArchived: false,
      isDeceased: animalData.isDeceased ?? false,
      isDeparted: animalData.isDeparted ?? false,
      ...getCreationStamp(),
      ...getModificationStamp(),
    }

    try {
      // Photo recadrée à la création : envoyée dans Storage comme à la
      // modification (`_updateAnimal`), jamais stockée en data URI dans la
      // fiche (recette 12.1.0, COL-10).
      if (newEntry.animalProfilePicture?.shouldUpload) {
        newEntry = await this.uploadPhotoAndPopulateUrl(newEntry)
      }
      await db.collection("animals").doc(uuid).set(newEntry)
      return { success: true, data: newEntry }
    } catch (error) {
      console.error(error)
      return { success: false, data: error }
    }
  }

  async _generateRandomAnimals(amount, genericAnimal, speciesList) {
    for (let index = 0; index < amount; index++) {
      let newAnimal = { ...genericAnimal }
      newAnimal.animalName = uniqueNamesGenerator({ dictionaries: [adjectives, colors] })

      let specie = speciesList[Math.floor(Math.random() * speciesList.length)]
      newAnimal = {
        ...newAnimal,
        ...specie,
      }

      let randomizedAnimal = { ...specie, ...newAnimal }
      this._setNewAnimal(randomizedAnimal)
    }
  }

  async resizeImage(imageFile, maxWidth, maxHeight, quality) {
    try {
      const resizedFile = await imageCompression(imageFile, {
        // browser-image-compression exposes one longest-edge constraint. The
        // profile crop is already 4:3, so 1200 preserves the intended 1200×900
        // reference instead of shrinking it once more to 900×675.
        maxWidthOrHeight: Math.max(maxWidth, maxHeight),
        initialQuality: quality,
        useWebWorker: true,
      })
      return resizedFile
    } catch (error) {
      console.log(error)
      return null
    }
  }

  async uploadPhotoAndPopulateUrl(animalData) {
    if (_.has(animalData, "animalProfilePicture")) {
      if (animalData.animalProfilePicture.shouldUpload) {
        let url = null
        let now = moment().unix()
        function handleFinish(foo, bar, newUrl) {
          url = newUrl
        }

        let file = await cdn.urltoFile(animalData.animalProfilePicture.uri, `profile.jpg`, "image/jpg")
        // The identity reference is a 4:3 operational image. Preserve enough
        // detail for animal recognition while keeping the payload bounded.
        file = await this.resizeImage(file, 1200, 900, 0.86)
        let storageRef = await cdn._handleUploadFile(
          null,
          file,
          null,
          `${animalData.zooId}/enclosures/${animalData.animalId}/profil/${now}/`,
          animalData.animalId,
          () => {},
          handleFinish,
          (a) => {
            console.log(a)
          },
          undefined,
          `profile.jpg`
        )

        while (url === null) {
          await new Promise((resolve) => setTimeout(resolve, 500))
          console.log(url)
        }

        animalData.animalProfilePicture = {
          bddRef: storageRef,
          uri: url,
        }
      } else {
        animalData.animalProfilePicture = false
      }
    } else {
      animalData.animalProfilePicture = false
    }

    delete animalData.activeFile
    return animalData
  }

  async _updateAnimal(animalData) {
    try {
      let db = initDb()
      if (animalData.animalProfilePicture?.shouldUpload) {
        animalData = await this.uploadPhotoAndPopulateUrl(animalData)
      }
      animalData = { ...animalData, ...getModificationStamp() }
      await db.collection("animals").doc(animalData.animalId).update(animalData)
      return { success: true, data: animalData }
    } catch (error) {
      console.log(error)
      return { success: false, data: error }
    }
  }

  // Update ciblé : masquage + ordre d'affichage des paramètres santé de l'individu
  // (animals/{id}.healthParametersConfig — ne touche à rien d'autre du doc).
  async _setAnimalHealthParametersConfig(animalId, config) {
    try {
      let db = initDb()
      await db.collection("animals").doc(animalId).update({
        healthParametersConfig: config || null,
      })
      return { success: true }
    } catch (error) {
      console.log(error)
      return { success: false, data: error }
    }
  }

  // Mémorise qu'un ensemble d'individus ne sont pas des doublons entre eux
  // (champ additif notDuplicateOf, consommé par la détection de doublons)
  async _markAnimalsNotDuplicate(animalIds, { expectedZooId } = {}) {
    try {
      let db = initDb()
      const ids = [...new Set((animalIds || [])
        .map((animalId) => typeof animalId === "string" ? animalId.trim() : "")
        .filter(Boolean))]
      if (ids.length < 2) {
        return { success: false, code: "INVALID_ANIMAL_IDS" }
      }

      // Le chemin V2 fournit toujours le zoo issu de la session authentifiée.
      // Relire chaque fiche juste avant le batch évite qu'un ID étranger ou
      // supprimé soit mêlé à la confirmation. Les règles Firestore restent la
      // dernière autorité et rendent le batch atomique si l'état change ensuite.
      if (typeof expectedZooId === "string" && expectedZooId.trim()) {
        const resolvedZooId = expectedZooId.trim()
        const snapshots = await Promise.all(ids.map((animalId) =>
          db.collection("animals").doc(animalId).get()
        ))
        const scoped = snapshots.every((snapshot) =>
          snapshot.exists && snapshot.data()?.zooId === resolvedZooId
        )
        if (!scoped) {
          return { success: false, code: "ANIMAL_ZOO_SCOPE_MISMATCH" }
        }
      }

      const batch = db.batch()
      ids.forEach((animalId) => {
        const others = ids.filter((id) => id !== animalId)
        batch.update(db.collection("animals").doc(animalId), {
          notDuplicateOf: firebase.firestore.FieldValue.arrayUnion(...others),
        })
      })
      await batch.commit()
      return { success: true }
    } catch (error) {
      console.log(error)
      return { success: false, data: error }
    }
  }
}
